/**
 *  @class
 *  @function SizePicker
 *
 *  Footwear size picker. Two controls over one <select>:
 *    - size system tabs        (us | uk | eu)
 *    - size grid               (radiogroup, women's number over men's number)
 *
 *  There is no women's / men's toggle: every button carries both readings for
 *  the active size system, so one grid serves both.
 *
 *  The <select> is what assets/product.js (VariantSelects) reads, so selecting
 *  a size goes through the theme's own variant machinery: hidden id input,
 *  ?variant= URL, price / inventory / media re-render, sticky bar sync.
 *
 *  Nothing is selected on load; add to cart stays disabled until it is.
 */
if (!customElements.get('size-picker')) {
  class SizePicker extends HTMLElement {
    constructor() {
      super();
      this.rows = {};
      this.selected = null;
      this.availability = {};
      this.initialised = false;
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
    }

    connectedCallback() {
      if (!this.initialised) {
        if (!this.setup()) return;
        this.initialised = true;
      }

      // Registered on every connect, not once, so moving the picker in the DOM
      // cannot leave it permanently deaf to variant changes.
      document.addEventListener('product:variant-change', this.onVariantChange);
      document.addEventListener('submit', this.onFormSubmit, true);
    }

    disconnectedCallback() {
      document.removeEventListener('product:variant-change', this.onVariantChange);
      document.removeEventListener('submit', this.onFormSubmit, true);
    }

    setup() {
      this.variantSelects = this.closest('variant-selects');
      this.sectionId = this.variantSelects ? this.variantSelects.dataset.section : null;
      this.sizeIndex = parseInt(this.dataset.optionPosition, 10) - 1;

      this.select = this.querySelector('[data-size-picker-select]');
      this.grid = this.querySelector('[data-size-picker-grid]');
      this.sizeButtons = Array.from(this.querySelectorAll('.size-picker__size'));
      this.selection = this.querySelector('[data-size-picker-selection]');
      this.announcer = this.querySelector('[data-size-picker-announcer]');

      if (!this.select || !this.grid) return false;

      this.strings = {
        selectSize: this.dataset.stringSelectSize || 'Select a size',
        soldOut: this.dataset.stringSoldOut || 'Sold out',
        womens: this.dataset.stringWomens || "Women's",
        mens: this.dataset.stringMens || "Men's",
        womensShort: this.dataset.stringWomensShort || 'W',
        mensShort: this.dataset.stringMensShort || 'M',
        selected: this.dataset.stringSelected || 'selected'
      };

      this.system = ['us', 'uk', 'eu'].includes(this.dataset.defaultSystem) ? this.dataset.defaultSystem : 'us';

      this.rows = this.readChart();
      this.bindControls();

      // The theme renders the first available variant server-side. Nothing is
      // chosen yet, so clear the select and gate add to cart.
      this.select.value = '';
      this.availability = this.computeAvailability();
      this.render();
      this.gateAddToCart();

      return true;
    }

    readChart() {
      const node = this.querySelector('[data-size-chart]');
      if (!node) return {};
      try {
        const data = JSON.parse(node.textContent);
        return data && data.rows ? data.rows : {};
      } catch (e) {
        return {};
      }
    }

    /** One-time handlers on our own controls. */
    bindControls() {
      this.querySelectorAll('.size-picker__system').forEach((button) => {
        button.addEventListener('click', () => this.setSystem(button.dataset.system));
      });

      this.sizeButtons.forEach((button) => {
        button.addEventListener('click', () => this.setSize(button.dataset.value));
      });

      this.grid.addEventListener('keydown', this.onGridKeydown.bind(this));
    }

    /**
     * Fires at the end of every VariantSelects.onVariantChange, from this
     * section or any other on the page.
     */
    onVariantChange() {
      // The <select> is the source of truth, not event.detail.variant:
      // updateOther() in product.js writes into it from the sticky bar without
      // dispatching change, and leaves it at '' when a combination has no
      // variant. Reading it keeps this.selected honest in both directions.
      this.selected = this.select.value || null;
      this.availability = this.computeAvailability();
      this.render();
      this.gateAddToCart();
    }

    /* ----------------------------------------------------------------- data */

    /**
     * Read the variant JSON straight from the DOM rather than calling
     * VariantSelects.getVariantData(): this snippet's <script> tag precedes
     * product.js in document order, so on first run <variant-selects> may not
     * be upgraded yet. The chart script above is deliberately not
     * type="application/json" so this selector only ever finds the variants.
     */
    getVariants() {
      if (this.variants) return this.variants;
      if (!this.variantSelects) return [];

      if (typeof this.variantSelects.getVariantData === 'function') {
        this.variants = this.variantSelects.getVariantData();
        return this.variants;
      }

      const node = this.variantSelects.querySelector('script[type="application/json"]');
      if (!node) return [];

      try {
        this.variants = JSON.parse(node.textContent);
      } catch (e) {
        this.variants = [];
      }
      return this.variants;
    }

    /**
     * Whatever is currently chosen for the *other* options (colour, width …),
     * as { optionIndex: value }. Read from the sibling fieldsets so it always
     * matches what product.js thinks is selected. An option with no selection
     * reports '', which computeAvailability() then ignores.
     */
    getOtherSelections() {
      const selections = {};
      if (!this.variantSelects) return selections;

      Array.from(this.variantSelects.querySelectorAll('fieldset')).forEach((fieldset, index) => {
        if (index === this.sizeIndex) return;

        const select = fieldset.querySelector('select');
        if (select) {
          selections[index] = select.value;
          return;
        }
        const checked = fieldset.querySelector('input:checked');
        selections[index] = checked ? checked.value : '';
      });

      return selections;
    }

    computeAvailability() {
      const variants = this.getVariants();
      if (!variants.length) return {};

      // Default every size to unavailable. A size with no variant at all for
      // the chosen colour must not look selectable — picking it would set a
      // combination that does not exist.
      const availability = {};
      this.sizeButtons.forEach((button) => {
        availability[button.dataset.value] = false;
      });

      // Only constrain on options that actually have a selection. Matching an
      // unselected option against '' would find no variants and wrongly mark
      // every size sold out.
      const others = Object.entries(this.getOtherSelections()).filter(([, value]) => value);

      variants.forEach((variant) => {
        if (!variant.options) return;

        const matchesOthers = others.every(([index, value]) => variant.options[index] === value);
        if (!matchesOthers) return;

        if (variant.available) availability[variant.options[this.sizeIndex]] = true;
      });

      return availability;
    }

    /** The number to print for one fit, or null when that size has no number. */
    labelFor(value, fit, system) {
      const row = this.rows[value];
      if (!row) return null;

      // ukM/ukW only come from the metaobject fallback; a hand-written chart
      // row carries the single `uk` column the brief specified.
      if (system === 'uk') return (fit === 'women' ? row.ukW : row.ukM) || row.uk || null;
      if (system === 'eu') return (fit === 'women' ? row.euW : row.euM) || null;
      return (fit === 'women' ? row.usW : row.usM) || null;
    }

    /**
     * The nearest view that actually has a number for this size, used when the
     * active system has no number for either fit — a men's-only size has no
     * women's EU number, and a chart may be missing a system outright.
     */
    resolveLabel(value) {
      const candidates = [
        ['women', this.system],
        ['men', this.system],
        ['women', 'us'],
        ['men', 'us']
      ];

      for (let i = 0; i < candidates.length; i += 1) {
        const [fit, system] = candidates[i];
        const label = this.labelFor(value, fit, system);
        if (label) return { fit, system, label };
      }
      return null;
    }

    fitLabel(fit) {
      return fit === 'women' ? this.strings.womens : this.strings.mens;
    }

    /**
     * What one button shows and what a screen reader calls it. "W8" over "M7"
     * is readable on a 60px button but says nothing out loud, so the spoken
     * name spells both readings out in full.
     */
    describe(value) {
      const hasRow = Boolean(this.rows[value]);
      if (!hasRow) return { primary: value, secondary: '', name: value };

      const system = this.system.toUpperCase();
      const women = this.labelFor(value, 'women', this.system);
      const men = this.labelFor(value, 'men', this.system);

      if (!women && !men) {
        const resolved = this.resolveLabel(value);
        if (!resolved) return { primary: value, secondary: '', name: value };
        return {
          primary: `${resolved.label}`,
          secondary: '',
          name: `${this.fitLabel(resolved.fit)} ${resolved.system.toUpperCase()} ${resolved.label}`
        };
      }

      // Men's is the headline size, women's the second line under it.
      const mensLine = men ? `${this.strings.mensShort}${men}` : null;
      const womensLine = women ? `${this.strings.womensShort}${women}` : null;
      const names = [];
      if (men) names.push(`${this.strings.mens} ${system} ${men}`);
      if (women) names.push(`${this.strings.womens} ${system} ${women}`);

      return {
        primary: mensLine || womensLine,
        secondary: mensLine && womensLine ? womensLine : '',
        name: names.join(', ')
      };
    }

    /* --------------------------------------------------------------- render */

    render() {
      this.sizeButtons.forEach((button) => {
        const value = button.dataset.value;
        const hasRow = Boolean(this.rows[value]);
        const soldOut = this.availability[value] === false;
        const isSelected = this.selected === value;
        const hasNumber = hasRow
          ? Boolean(this.labelFor(value, 'women', this.system) || this.labelFor(value, 'men', this.system))
          : true;

        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-checked', isSelected ? 'true' : 'false');

        // A row that exists but has no number for either fit in this system
        // (EU on a chart with no EU column) is hidden, which is the point of
        // leaving those cells empty — unless it is the selected size, which
        // must never become invisible. A size with no row at all is always
        // shown: never hide a real size because its chart data is missing.
        if (!hasNumber && !isSelected) {
          button.hidden = true;
          button.setAttribute('aria-hidden', 'true');
          button.tabIndex = -1;
          return;
        }

        button.hidden = false;
        button.removeAttribute('aria-hidden');

        const described = this.describe(value);
        button.querySelector('[data-size-primary]').textContent = described.primary;
        button.querySelector('[data-size-secondary]').textContent = described.secondary;
        button.classList.toggle('is-sold-out', soldOut);
        button.setAttribute('aria-label', soldOut ? `${described.name} ${this.strings.soldOut}` : described.name);

        if (soldOut) {
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('aria-disabled');
        }
      });

      this.renderSelection();
      this.setRovingTabIndex();

      this.querySelectorAll('.size-picker__system').forEach((button) => {
        button.setAttribute('aria-pressed', button.dataset.system === this.system ? 'true' : 'false');
      });
    }

    /** Exactly one button in the radiogroup is tabbable. */
    setRovingTabIndex() {
      const focusable = this.focusableButtons();
      this.sizeButtons.forEach((button) => {
        button.tabIndex = -1;
      });

      const selectedButton = focusable.find((button) => button.dataset.value === this.selected);
      const target = selectedButton || focusable.find((button) => !button.classList.contains('is-sold-out')) || focusable[0];
      if (target) target.tabIndex = 0;
    }

    focusableButtons() {
      return this.sizeButtons.filter((button) => !button.hidden);
    }

    /**
     * The chosen size, spelled out beside the step heading: "US Women's 9 /
     * Men's 8". Kept out of the radiogroup's accessible name on purpose — see
     * the comment in the snippet.
     */
    renderSelection() {
      if (!this.selection) return;

      if (!this.selected) {
        this.selection.textContent = '';
        this.selection.hidden = true;
        return;
      }

      this.selection.textContent = this.summarise(this.selected);
      this.selection.hidden = false;
    }

    /**
     * "US Women's 9 / Men's 8" — the system named once up front, then each fit.
     * Falls back to whichever fit the chart has a number for, and finally to the
     * raw option value.
     */
    summarise(value) {
      if (!this.rows[value]) return value;

      const women = this.labelFor(value, 'women', this.system);
      const men = this.labelFor(value, 'men', this.system);

      if (!women && !men) {
        const resolved = this.resolveLabel(value);
        if (!resolved) return value;
        return `${resolved.system.toUpperCase()} ${this.fitLabel(resolved.fit)} ${resolved.label}`;
      }

      const parts = [];
      if (men) parts.push(`${this.strings.mens} ${men}`);
      if (women) parts.push(`${this.strings.womens} ${women}`);
      return `${this.system.toUpperCase()} ${parts.join(' / ')}`;
    }

    announce(message) {
      if (this.announcer) this.announcer.textContent = message;
    }

    /* ---------------------------------------------------------------- state */

    setSystem(system) {
      if (!['us', 'uk', 'eu'].includes(system)) return;
      if (system === this.system) return;

      // Relabel only. The selection is a Shopify option value, not a number, so
      // the same physical boot stays selected.
      this.system = system;
      this.render();

      if (this.selected) this.announce(this.describe(this.selected).name);
    }

    setSize(value) {
      if (this.availability[value] === false) return;

      this.selected = value;
      this.select.value = value;

      // Hands off to VariantSelects.onVariantChange, which owns the variant id,
      // URL, price, media and button state from here.
      this.select.dispatchEvent(new Event('change', { bubbles: true }));

      this.render();
      this.announce(`${this.describe(value).name} ${this.strings.selected}.`);
    }

    /* ------------------------------------------------------------- keyboard */

    /**
     * Arrow keys move focus; Space / Enter select. Selection deliberately does
     * not follow focus: each selection re-fetches the section and rewrites the
     * URL, so arrowing across ten sizes should not fire ten variant changes.
     */
    onGridKeydown(event) {
      const buttons = this.focusableButtons();
      if (!buttons.length) return;

      const currentIndex = buttons.indexOf(document.activeElement);
      let nextIndex = null;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % buttons.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = currentIndex < 0 ? buttons.length - 1 : (currentIndex - 1 + buttons.length) % buttons.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = buttons.length - 1;
          break;
        case ' ':
        case 'Enter':
          if (currentIndex >= 0) {
            event.preventDefault();
            this.setSize(buttons[currentIndex].dataset.value);
          }
          return;
        default:
          return;
      }

      event.preventDefault();
      buttons.forEach((button) => {
        button.tabIndex = -1;
      });
      buttons[nextIndex].tabIndex = 0;
      buttons[nextIndex].focus();
    }

    /* ----------------------------------------------------------- add to cart */

    /** Both add-to-cart buttons for this section: the main one and the sticky bar. */
    sectionButtons() {
      if (!this.sectionId) return [];
      return Array.from(
        document.querySelectorAll(`product-form[data-section="${this.sectionId}"] .single-add-to-cart-button`)
      );
    }

    /**
     * With no size chosen there is no variant, so product.js runs its
     * setUnavailable() path: "Unavailable" and a hidden price. Re-assert the
     * real state — "Select a size", price visible — without touching
     * product.js. Scoped to this section so a quick view elsewhere on the page
     * keeps its own button copy.
     */
    gateAddToCart() {
      if (this.selected) return;

      this.sectionButtons().forEach((button) => {
        button.setAttribute('disabled', 'disabled');
        button.classList.remove('sold-out', 'loading');
        const text = button.querySelector('.single-add-to-cart-button--text');
        if (text) text.textContent = this.strings.selectSize;
        // "Select a size · $135" would name a price for a size nobody picked.
        const price = button.querySelector('[data-add-to-cart-price]');
        if (price) price.hidden = true;
      });

      if (!this.sectionId) return;
      [`price-${this.sectionId}`, `price-${this.sectionId}--sticky`].forEach((id) => {
        const price = document.getElementById(id);
        if (price) price.classList.remove('visibility-hidden');
      });
    }

    /**
     * Last line of defence, in the capture phase at document level so it runs
     * before the form's own handler. toggleAddButton() in product.js is
     * document-wide and runs from an async fetch, so another section on the
     * page (a quick view) can strip the disabled attribute off this form's
     * button after it was gated. Block the submit itself while no size is
     * chosen, rather than trusting the button's state.
     */
    onFormSubmit(event) {
      if (this.selected || !this.sectionId) return;

      const form = event.target;
      if (!form || form.id !== `product-form-${this.sectionId}`) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      this.gateAddToCart();
      this.announce(this.strings.selectSize);

      const target = this.focusableButtons().find((button) => !button.classList.contains('is-sold-out'));
      if (target) target.focus();
    }
  }

  customElements.define('size-picker', SizePicker);
}
